//
// src/pages/AdminBilling.js
//
// Production workspace billing and subscription management.
//
// Responsibilities:
// - Display authoritative workspace licence/subscription status
// - Display available subscription tiers
// - Start Paynow checkout
// - Verify Paynow payment after provider return
// - Synchronize tier/licence state with the rest of the application
//
// Licensing policy:
// - Authentication and licensing are separate concerns.
// - Expired/inactive/unlicensed workspaces remain accessible.
// - Customers are never logged out because payment is due.
// - Only creation of a new sale is blocked by the backend.
// - Billing and Paynow renewal remain available at all times.
//
// Important:
// - /billing/status is the single authoritative status endpoint.
// - This component does NOT call /licenses/{tenant}.
// - This component does NOT seed or auto-repair licences.
// - The centralized API client manages Authorization and X-Tenant-ID.
//

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";

import {
  CheckCircle,
  CreditCard,
  ErrorOutline,
  Receipt,
  Refresh,
  Shield,
  Star,
  Warning,
  WorkspacePremium,
} from "@mui/icons-material";

import API from "../api";


// =====================================================
// Subscription Plans
// =====================================================

const PLAN_TIERS = [
  {
    id: "silver",
    name: "Silver Tier",
    price: "$50.00",
    color: "default",
    icon: (
      <Shield color="action" />
    ),
    badgeText: "ESSENTIAL",
    features: [
      "Cashier POS register",
      "Product and stock management",
      "Daily sales records",
      "Online billing and subscription renewal",
    ],
  },

  {
    id: "gold",
    name: "Gold Tier",
    price: "$100.00",
    color: "warning",
    icon: (
      <Star color="warning" />
    ),
    badgeText: "MOST POPULAR",
    features: [
      "Everything included in Silver",
      "Users and staff management",
      "Reports and business analytics",
      "Advanced inventory controls",
    ],
  },

  {
    id: "platinum",
    name: "Platinum Tier",
    price: "$150.00",
    color: "secondary",
    icon: (
      <WorkspacePremium color="secondary" />
    ),
    badgeText: "ENTERPRISE",
    features: [
      "Everything included in Gold",
      "Audit logs and security ledger",
      "Multi-branch and priority support",
      "Complete operational feature access",
    ],
  },
];


// =====================================================
// Helpers
// =====================================================

function normalizeTier(value) {
  const tier =
    String(value || "")
      .trim()
      .toLowerCase();

  // Compatibility with existing trial records.
  if (
    tier === "standard" ||
    tier === "basic"
  ) {
    return "silver";
  }

  if (
    tier === "silver" ||
    tier === "gold" ||
    tier === "platinum"
  ) {
    return tier;
  }

  return "silver";
}


function normalizeStatus(value) {
  return String(
    value || "unknown"
  )
    .trim()
    .toLowerCase();
}


function extractErrorMessage(
  error,
  fallbackMessage =
    "We were unable to complete your request."
) {
  const detail =
    error?.response?.data?.detail;

  if (
    typeof detail === "string"
  ) {
    return detail;
  }

  if (
    detail &&
    typeof detail === "object"
  ) {
    if (
      typeof detail.message ===
      "string"
    ) {
      return detail.message;
    }

    if (
      typeof detail.msg ===
      "string"
    ) {
      return detail.msg;
    }
  }

  if (
    Array.isArray(detail) &&
    detail.length > 0
  ) {
    const firstError =
      detail[0];

    if (
      typeof firstError === "string"
    ) {
      return firstError;
    }

    if (
      firstError &&
      typeof firstError === "object"
    ) {
      const message =
        firstError.message ||
        firstError.msg;

      if (message) {
        return String(message);
      }
    }
  }

  return (
    error?.message ||
    fallbackMessage
  );
}


function formatDate(value) {
  if (!value) {
    return "Not available";
  }

  const parsed =
    new Date(value);

  if (
    Number.isNaN(
      parsed.getTime()
    )
  ) {
    return "Not available";
  }

  return parsed.toLocaleDateString();
}


function formatDateTime(value) {
  if (!value) {
    return "Not available";
  }

  const parsed =
    new Date(value);

  if (
    Number.isNaN(
      parsed.getTime()
    )
  ) {
    return "Not available";
  }

  return parsed.toLocaleString();
}


function maskLicenseToken(value) {
  const token =
    String(value || "")
      .trim();

  if (!token) {
    return "No active token available";
  }

  if (token.length <= 12) {
    return token;
  }

  return (
    `${token.slice(0, 8)}` +
    "••••••••" +
    `${token.slice(-4)}`
  );
}


function getStatusPresentation(
  license
) {
  const currentStatus =
    normalizeStatus(
      license?.status
    );

  const isActive =
    Boolean(
      license?.is_active
    );

  const canCreateSales =
    license?.can_create_sales !==
    false;

  const renewalRequired =
    Boolean(
      license?.renewal_required
    );


  if (
    isActive &&
    canCreateSales &&
    !renewalRequired
  ) {
    return {
      severity: "success",
      chipColor: "success",
      chipLabel:
        "SUBSCRIPTION ACTIVE",
      heading:
        "Your subscription is active",
      description:
        license?.message ||
        (
          "Your workspace is fully operational " +
          "and new sales can be processed."
        ),
    };
  }


  if (
    currentStatus === "expired"
  ) {
    return {
      severity: "warning",
      chipColor: "warning",
      chipLabel:
        "RENEWAL DUE",
      heading:
        "Your subscription has expired",
      description:
        license?.message ||
        (
          "Your workspace remains available. " +
          "New sales are temporarily paused until " +
          "the subscription is renewed."
        ),
    };
  }


  if (
    currentStatus === "inactive"
  ) {
    return {
      severity: "warning",
      chipColor: "warning",
      chipLabel:
        "SUBSCRIPTION INACTIVE",
      heading:
        "Your subscription needs attention",
      description:
        license?.message ||
        (
          "You can continue using your workspace. " +
          "Please renew your subscription to resume new sales."
        ),
    };
  }


  if (
    currentStatus ===
    "unlicensed"
  ) {
    return {
      severity: "info",
      chipColor: "default",
      chipLabel:
        "SUBSCRIPTION REQUIRED",
      heading:
        "Choose a subscription plan",
      description:
        license?.message ||
        (
          "Your workspace remains accessible. " +
          "Select a plan below when you are ready to enable new sales."
        ),
    };
  }


  return {
    severity: "info",
    chipColor: "default",
    chipLabel:
      "STATUS AVAILABLE",
    heading:
      "Workspace subscription",
    description:
      license?.message ||
      (
        "Review your subscription information below."
      ),
  };
}


// =====================================================
// Component
// =====================================================

export default function AdminBilling({
  loggedUser,
}) {
  // ===================================================
  // State
  // ===================================================

  const [
    license,
    setLicense,
  ] = useState(null);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    refreshing,
    setRefreshing,
  ] = useState(false);

  const [
    checkoutLoading,
    setCheckoutLoading,
  ] = useState(null);

  const [
    verifyingPayment,
    setVerifyingPayment,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const [
    notification,
    setNotification,
  ] = useState({
    open: false,
    message: "",
    severity: "info",
  });


  // ===================================================
  // Workspace Context
  // ===================================================

  const tenantId =
    useMemo(() => {
      return String(
        localStorage.getItem(
          "tenant_id"
        ) ||
        loggedUser?.tenant_id ||
        ""
      )
        .trim()
        .toLowerCase();
    }, [
      loggedUser?.tenant_id,
    ]);


  // ===================================================
  // Notifications
  // ===================================================

  const showNotification =
    useCallback(
      (
        message,
        severity = "info"
      ) => {
        setNotification({
          open: true,
          message:
            String(
              message ||
              ""
            ),
          severity,
        });
      },
      []
    );


  const closeNotification =
    useCallback(() => {
      setNotification(
        (previous) => ({
          ...previous,
          open: false,
        })
      );
    }, []);


  // ===================================================
  // Shared Application State
  // ===================================================

  const updateApplicationLicenseState =
    useCallback(
      (licenseData) => {
        if (!licenseData) {
          return;
        }


        const cleanTier =
          normalizeTier(
            licenseData.tier ||
            licenseData.plan_type
          );


        localStorage.setItem(
          "tenant_tier",
          cleanTier
        );


        // Update cached user tier without changing
        // any authentication information.
        try {
          const storedUser =
            JSON.parse(
              localStorage.getItem(
                "user"
              ) || "{}"
            );


          if (
            storedUser &&
            typeof storedUser ===
            "object"
          ) {
            storedUser.tier =
              cleanTier;


            localStorage.setItem(
              "user",
              JSON.stringify(
                storedUser
              )
            );
          }

        } catch (parseError) {
          console.warn(
            "Stored user tier could not be updated:",
            parseError
          );
        }


        window.dispatchEvent(
          new CustomEvent(
            "tierUpdated",
            {
              detail: {
                tier:
                  cleanTier,
              },
            }
          )
        );


        window.dispatchEvent(
          new CustomEvent(
            "licenseUpdated",
            {
              detail: {
                ...licenseData,
                tier:
                  cleanTier,
              },
            }
          )
        );
      },
      []
    );


  // ===================================================
  // Authoritative Licence Status
  // ===================================================

  const fetchLicenseStatus =
    useCallback(
      async ({
        silent = false,
      } = {}) => {
        if (silent) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }


        setError("");


        try {
          const response =
            await API.get(
              "/billing/status"
            );


          const licenseData =
            response.data || {};


          setLicense(
            licenseData
          );


          updateApplicationLicenseState(
            licenseData
          );


          return licenseData;

        } catch (requestError) {
          console.error(
            "Unable to retrieve billing status:",
            requestError
          );


          const statusCode =
            requestError?.response
              ?.status;


          // Do not convert an entitlement/licensing issue
          // into an authentication logout.
          if (
            statusCode === 402
          ) {
            const message =
              extractErrorMessage(
                requestError,
                (
                  "Your subscription requires renewal. " +
                  "You remain signed in and can renew below."
                )
              );


            setError(
              message
            );


            return null;
          }


          setError(
            extractErrorMessage(
              requestError,
              (
                "We could not refresh your subscription status. " +
                "Please try again in a moment."
              )
            )
          );


          return null;

        } finally {
          setLoading(false);
          setRefreshing(false);
        }
      },
      [
        updateApplicationLicenseState,
      ]
    );


  // ===================================================
  // Paynow Return Verification
  // ===================================================

  useEffect(() => {
    let mounted =
      true;


    const processBillingReturn =
      async () => {
        const params =
          new URLSearchParams(
            window.location.search
          );


        const checkoutStatus =
          normalizeStatus(
            params.get(
              "status"
            )
          );


        const reference =
          params.get(
            "reference"
          ) ||
          params.get(
            "checkout_id"
          );


        try {
          if (
            (
              checkoutStatus ===
                "success" ||
              checkoutStatus ===
                "completed"
            ) &&
            reference
          ) {
            if (!mounted) {
              return;
            }


            setVerifyingPayment(
              true
            );


            showNotification(
              (
                "Thank you. Your payment was received. " +
                "We are confirming it with Paynow now."
              ),
              "info"
            );


            const response =
              await API.get(
                "/billing/verify-paynow-payment",
                {
                  params: {
                    reference,
                  },
                }
              );


            if (!mounted) {
              return;
            }


            const result =
              response.data || {};


            if (
              result.paid &&
              result.license_applied
            ) {
              showNotification(
                result.message ||
                (
                  "Payment confirmed. " +
                  "Your subscription has been activated successfully."
                ),
                "success"
              );

            } else if (
              result.paid
            ) {
              showNotification(
                result.message ||
                (
                  "Payment has been confirmed. " +
                  "Your workspace subscription is being updated."
                ),
                "success"
              );

            } else {
              showNotification(
                result.message ||
                (
                  "Your payment is still being confirmed. " +
                  "You may refresh the subscription status shortly."
                ),
                "warning"
              );
            }


            await fetchLicenseStatus({
              silent: true,
            });

          } else if (
            checkoutStatus ===
            "cancelled"
          ) {
            showNotification(
              (
                "The Paynow checkout was cancelled. " +
                "No changes were made to your subscription."
              ),
              "info"
            );


            await fetchLicenseStatus();

          } else {
            await fetchLicenseStatus();
          }

        } catch (verificationError) {
          if (!mounted) {
            return;
          }


          console.error(
            "Paynow verification failed:",
            verificationError
          );


          setError(
            extractErrorMessage(
              verificationError,
              (
                "We could not complete payment verification. " +
                "Please refresh the subscription status before making another payment."
              )
            )
          );


          await fetchLicenseStatus({
            silent: true,
          });

        } finally {
          if (mounted) {
            setVerifyingPayment(
              false
            );


            // Remove provider query parameters after
            // they have been processed.
            if (
              window.location.search
            ) {
              window.history.replaceState(
                {},
                document.title,
                window.location.pathname
              );
            }
          }
        }
      };


    processBillingReturn();


    return () => {
      mounted = false;
    };

  }, [
    fetchLicenseStatus,
    showNotification,
  ]);


  // ===================================================
  // Paynow Checkout
  // ===================================================

  const handleStartSubscription =
    useCallback(
      async (planTier) => {
        const cleanTier =
          normalizeTier(
            planTier
          );


        setCheckoutLoading(
          cleanTier
        );


        setError("");


        try {
          const response =
            await API.post(
              "/billing/create-checkout-session",
              {},
              {
                params: {
                  tier:
                    cleanTier,
                },
              }
            );


          const checkoutUrl =
            response.data
              ?.checkout_url;


          if (!checkoutUrl) {
            throw new Error(
              (
                "Paynow did not return a checkout address. " +
                "Please try again."
              )
            );
          }


          window.location.assign(
            checkoutUrl
          );

        } catch (checkoutError) {
          console.error(
            "Paynow checkout could not be started:",
            checkoutError
          );


          const statusCode =
            checkoutError?.response
              ?.status;


          if (
            statusCode === 402
          ) {
            // Billing itself must remain accessible.
            setError(
              extractErrorMessage(
                checkoutError,
                (
                  "Your subscription requires renewal. " +
                  "Please select a plan and try again."
                )
              )
            );

            return;
          }


          setError(
            extractErrorMessage(
              checkoutError,
              (
                "We could not open Paynow checkout. " +
                "Please check your connection and try again."
              )
            )
          );

        } finally {
          setCheckoutLoading(
            null
          );
        }
      },
      []
    );


  // ===================================================
  // Derived Licence State
  // ===================================================

  const currentTier =
    normalizeTier(
      license?.tier ||
      license?.plan_type ||
      localStorage.getItem(
        "tenant_tier"
      )
    );


  const currentStatus =
    normalizeStatus(
      license?.status
    );


  const isLicenseActive =
    Boolean(
      license?.is_active
    );


  const canCreateSales =
    license?.can_create_sales !==
    false;


  const renewalRequired =
    Boolean(
      license?.renewal_required
    ) ||
    currentStatus ===
      "expired" ||
    currentStatus ===
      "inactive" ||
    currentStatus ===
      "unlicensed";


  const daysRemaining =
    Number.isFinite(
      Number(
        license?.days_left
      )
    )
      ? Number(
          license?.days_left
        )
      : 0;


  const activeToken =
    license?.active_token ||
    license?.license_key ||
    "";


  const statusPresentation =
    getStatusPresentation(
      license
    );


  // ===================================================
  // Loading Screen
  // ===================================================

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent:
            "center",
          alignItems:
            "center",
          minHeight:
            "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }


  // ===================================================
  // Render
  // ===================================================

  return (
    <Container
      maxWidth="lg"
      sx={{
        mt: 4,
        mb: 6,
      }}
    >
      {/* =================================================
          Header
      ================================================= */}

      <Box
        sx={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems:
            "center",
          mb: 3,
          flexWrap:
            "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            fontWeight="bold"
          >
            Subscription & Billing
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.5,
            }}
          >
            Review your workspace subscription,
            choose a plan, or renew securely
            through Paynow.
          </Typography>
        </Box>


        <Button
          startIcon={
            refreshing
              ? (
                  <CircularProgress
                    size={18}
                    color="inherit"
                  />
                )
              : (
                  <Refresh />
                )
          }
          variant="outlined"
          onClick={() =>
            fetchLicenseStatus({
              silent: true,
            })
          }
          disabled={
            refreshing ||
            verifyingPayment
          }
          sx={{
            textTransform:
              "none",
            fontWeight:
              "bold",
          }}
        >
          Refresh Status
        </Button>
      </Box>


      {/* =================================================
          Errors
      ================================================= */}

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: 2,
          }}
          onClose={() =>
            setError("")
          }
        >
          {error}
        </Alert>
      )}


      {/* =================================================
          Subscription Status Message
      ================================================= */}

      <Alert
        severity={
          statusPresentation.severity
        }
        icon={
          renewalRequired
            ? (
                <Warning />
              )
            : (
                <CheckCircle />
              )
        }
        sx={{
          mb: 3,
          borderRadius: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight="bold"
        >
          {statusPresentation.heading}
        </Typography>

        <Typography
          variant="body2"
        >
          {statusPresentation.description}
        </Typography>


        {!canCreateSales && (
          <Typography
            variant="body2"
            sx={{
              mt: 1,
              fontWeight: 600,
            }}
          >
            You remain signed in and can continue
            accessing your workspace. Only new sale
            creation is temporarily unavailable until
            the subscription is renewed.
          </Typography>
        )}
      </Alert>


      {/* =================================================
          Workspace Overview
      ================================================= */}

      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 3,
          border:
            "1px solid",
          borderColor:
            renewalRequired
              ? "warning.light"
              : "success.light",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: {
              xs: "column",
              md: "row",
            },
            justifyContent:
              "space-between",
            alignItems: {
              xs: "flex-start",
              md: "center",
            },
            gap: 2,
          }}
        >
          <Box>
            <Stack
              direction="row"
              spacing={1.5}
              sx={{
                alignItems:
                  "center",
                mb: 1,
                flexWrap:
                  "wrap",
                gap: 1,
              }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
              >
                Workspace:{" "}
                {tenantId
                  ? tenantId.toUpperCase()
                  : "UNKNOWN"}
              </Typography>


              <Chip
                icon={
                  isLicenseActive
                    ? (
                        <CheckCircle />
                      )
                    : (
                        <ErrorOutline />
                      )
                }
                label={
                  statusPresentation
                    .chipLabel
                }
                color={
                  statusPresentation
                    .chipColor
                }
                size="small"
                sx={{
                  fontWeight:
                    "bold",
                }}
              />
            </Stack>


            <Typography
              variant="body2"
              color="text.secondary"
            >
              Current tier:{" "}
              <strong>
                {currentTier.toUpperCase()}
              </strong>

              {" · "}

              Sales:{" "}

              <strong>
                {canCreateSales
                  ? "Enabled"
                  : "Temporarily paused"}
              </strong>
            </Typography>


            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,
              }}
            >
              Signed in as{" "}
              <strong>
                {loggedUser?.name ||
                  loggedUser?.email ||
                  "User"}
              </strong>

              {loggedUser?.role
                ? ` (${loggedUser.role})`
                : ""}
            </Typography>


            {license?.expires_at && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 0.5,
                }}
              >
                Subscription expiry:{" "}
                <strong>
                  {formatDate(
                    license.expires_at
                  )}
                </strong>

                {" · "}

                Days remaining:{" "}

                <strong>
                  {daysRemaining}
                </strong>
              </Typography>
            )}
          </Box>


          {renewalRequired && (
            <Button
              variant="contained"
              color="warning"
              size="large"
              startIcon={
                checkoutLoading ===
                currentTier
                  ? (
                      <CircularProgress
                        size={18}
                        color="inherit"
                      />
                    )
                  : (
                      <CreditCard />
                    )
              }
              onClick={() =>
                handleStartSubscription(
                  currentTier
                )
              }
              disabled={
                checkoutLoading !==
                null
              }
              sx={{
                textTransform:
                  "none",
                fontWeight:
                  "bold",
                whiteSpace:
                  "nowrap",
              }}
            >
              Renew Current Plan
            </Button>
          )}
        </Box>
      </Paper>


      {/* =================================================
          Subscription Plans
      ================================================= */}

      <Typography
        variant="h5"
        fontWeight="bold"
        sx={{
          mb: 2,
        }}
      >
        Available Plans
      </Typography>


      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          mb: 3,
        }}
      >
        Select the plan that best fits your
        business. Paynow will open securely and
        return you to this page after payment.
      </Typography>


      <Grid
        container
        spacing={3}
        sx={{
          mb: 4,
        }}
      >
        {PLAN_TIERS.map(
          (plan) => {
            const isSelected =
              currentTier ===
                plan.id &&
              isLicenseActive &&
              !renewalRequired;


            const isThisCheckoutLoading =
              checkoutLoading ===
              plan.id;


            return (
              <Grid
                xs={12}
                md={4}
                key={
                  plan.id
                }
              >
                <Card
                  elevation={
                    isSelected
                      ? 4
                      : 1
                  }
                  sx={{
                    height:
                      "100%",
                    display:
                      "flex",
                    flexDirection:
                      "column",
                    borderRadius: 3,
                    border:
                      isSelected
                        ? "2px solid"
                        : "1px solid",
                    borderColor:
                      isSelected
                        ? "primary.main"
                        : "divider",
                  }}
                >
                  <CardContent
                    sx={{
                      p: 3,
                      flexGrow: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display:
                          "flex",
                        justifyContent:
                          "space-between",
                        alignItems:
                          "center",
                        mb: 1,
                        gap: 1,
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                          alignItems:
                            "center",
                        }}
                      >
                        {plan.icon}

                        <Typography
                          variant="h6"
                          fontWeight="bold"
                        >
                          {plan.name}
                        </Typography>
                      </Stack>


                      <Chip
                        label={
                          plan.badgeText
                        }
                        size="small"
                        color={
                          plan.color
                        }
                        variant="outlined"
                      />
                    </Box>


                    <Box
                      sx={{
                        display:
                          "flex",
                        alignItems:
                          "baseline",
                        my: 2,
                      }}
                    >
                      <Typography
                        variant="h4"
                        fontWeight="bold"
                        color="primary"
                      >
                        {plan.price}
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          ml: 1,
                        }}
                      >
                        / month
                      </Typography>
                    </Box>


                    <Divider
                      sx={{
                        my: 2,
                      }}
                    />


                    <Stack
                      spacing={1}
                    >
                      {plan.features.map(
                        (feature) => (
                          <Typography
                            key={
                              feature
                            }
                            variant="body2"
                            color="text.secondary"
                          >
                            ✓ {feature}
                          </Typography>
                        )
                      )}
                    </Stack>
                  </CardContent>


                  <Box
                    sx={{
                      p: 3,
                      pt: 0,
                    }}
                  >
                    <Button
                      variant={
                        isSelected
                          ? "contained"
                          : "outlined"
                      }
                      color={
                        plan.id ===
                        "platinum"
                          ? "secondary"
                          : (
                              plan.id ===
                              "gold"
                                ? "warning"
                                : "primary"
                            )
                      }
                      fullWidth
                      startIcon={
                        isThisCheckoutLoading
                          ? (
                              <CircularProgress
                                size={20}
                                color="inherit"
                              />
                            )
                          : (
                              <CreditCard />
                            )
                      }
                      onClick={() =>
                        handleStartSubscription(
                          plan.id
                        )
                      }
                      disabled={
                        checkoutLoading !==
                          null ||
                        verifyingPayment
                      }
                      sx={{
                        fontWeight:
                          "bold",
                        textTransform:
                          "none",
                      }}
                    >
                      {isThisCheckoutLoading
                        ? "Opening Paynow..."
                        : (
                            isSelected
                              ? "Renew Current Tier"
                              : `Select ${plan.name}`
                          )}
                    </Button>
                  </Box>
                </Card>
              </Grid>
            );
          }
        )}
      </Grid>


      {/* =================================================
          Licence Metadata
      ================================================= */}

      <Card
        elevation={1}
        sx={{
          borderRadius: 3,
          border:
            "1px solid",
          borderColor:
            "divider",
        }}
      >
        <CardContent
          sx={{
            p: 3,
          }}
        >
          <Typography
            variant="h6"
            fontWeight="bold"
            gutterBottom
          >
            Subscription Details
          </Typography>


          <Box
            sx={{
              backgroundColor:
                "action.hover",
              p: 2,
              borderRadius: 2,
              mb: 2,
            }}
          >
            <Grid
              container
              spacing={2}
            >
              <Grid
                xs={12}
                sm={3}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  PLAN
                </Typography>

                <Typography
                  variant="body2"
                  fontWeight="bold"
                >
                  {currentTier.toUpperCase()}
                </Typography>
              </Grid>


              <Grid
                xs={12}
                sm={3}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  STATUS
                </Typography>

                <Typography
                  variant="body2"
                  fontWeight="bold"
                >
                  {currentStatus
                    ? currentStatus.toUpperCase()
                    : "UNKNOWN"}
                </Typography>
              </Grid>


              <Grid
                xs={12}
                sm={3}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  LAST UPDATED
                </Typography>

                <Typography
                  variant="body2"
                  fontWeight="bold"
                >
                  {formatDateTime(
                    license?.updated_at ||
                    license?.activated_at
                  )}
                </Typography>
              </Grid>


              <Grid
                xs={12}
                sm={3}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  EXPIRES
                </Typography>

                <Typography
                  variant="body2"
                  fontWeight="bold"
                >
                  {formatDate(
                    license?.expires_at
                  )}
                </Typography>
              </Grid>


              <Grid
                xs={12}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{
                    mt: 1,
                  }}
                >
                  LICENCE TOKEN
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    wordBreak:
                      "break-all",
                  }}
                >
                  {maskLicenseToken(
                    activeToken
                  )}
                </Typography>
              </Grid>
            </Grid>
          </Box>


          <Button
            variant="text"
            color="inherit"
            startIcon={
              refreshing
                ? (
                    <CircularProgress
                      size={16}
                    />
                  )
                : (
                    <Receipt />
                  )
            }
            onClick={() =>
              fetchLicenseStatus({
                silent: true,
              })
            }
            disabled={
              refreshing
            }
            sx={{
              textTransform:
                "none",
            }}
          >
            Verify Subscription Status
          </Button>
        </CardContent>
      </Card>


      {/* =================================================
          Payment Verification Overlay
      ================================================= */}

      {verifyingPayment && (
        <Alert
          severity="info"
          sx={{
            mt: 3,
            borderRadius: 2,
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            sx={{
              alignItems:
                "center",
            }}
          >
            <CircularProgress
              size={18}
            />

            <Typography
              variant="body2"
            >
              Confirming your Paynow payment.
              Please keep this page open for a moment.
            </Typography>
          </Stack>
        </Alert>
      )}


      {/* =================================================
          Notification
      ================================================= */}

      <Snackbar
        open={
          notification.open
        }
        autoHideDuration={
          7000
        }
        onClose={
          closeNotification
        }
        anchorOrigin={{
          vertical:
            "bottom",
          horizontal:
            "right",
        }}
      >
        <Alert
          onClose={
            closeNotification
          }
          severity={
            notification.severity
          }
          variant="filled"
          sx={{
            width:
              "100%",
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}